import {
    Button,
    Classes,
    FormGroup,
    Icon,
    Intent,
    Radio,
    RadioGroup,
    Tooltip
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import classNames from "classnames";
import { useRegionConfig } from "in_queue/contexts/ClusterContext";
import { formatPower } from "in_queue/utils/formatting";
import { useState } from "react";
import css from "./BulkDownsizePopover.module.scss";
import { bulkDownsizeChanges, BulkDownsizeSide } from "./bulkProjectActions";
import {
    countErisBelowNris,
    NRIS_EXCEEDS_ERIS_TARIFF,
    ProjectPickerRow
} from "./projectPickerRow";
import { ReductionField } from "./ReductionField";
import { ProjectReductionChange } from "./useProjectAssumptionsEditor";

const DEFAULT_PERCENT = 50;

export const BulkDownsizePopover: React.FC<{
    readonly rows: ReadonlyArray<ProjectPickerRow>;
    readonly capacityOnlyProjectIds: ReadonlySet<string>;
    readonly energyLabel: string;
    readonly capacityLabel: string;
    readonly onApply: (changes: ReadonlyArray<ProjectReductionChange>) => void;
}> = ({
    rows,
    capacityOnlyProjectIds,
    energyLabel,
    capacityLabel,
    onApply
}) => {
    const [percent, setPercent] = useState(DEFAULT_PERCENT);
    const [applyTo, setApplyTo] = useState<BulkDownsizeSide>("both");

    const { changes, mwDelta, disabledReason } = bulkDownsizeChanges({
        rows,
        capacityOnlyProjectIds,
        percent,
        applyTo
    });
    const erisBelowNrisCount = useRegionConfig().strictSizeRulesEnabled
        ? countErisBelowNris({ rows, changes, capacityOnlyProjectIds })
        : 0;

    const sideLabel =
        applyTo === "both"
            ? `${energyLabel} and ${capacityLabel}`
            : applyTo === "eris"
            ? energyLabel
            : capacityLabel;

    return (
        <div className={css["BulkDownsizePopover"]}>
            <FormGroup
                inline
                label="Reduce by"
                className={css["BulkDownsizePopover-field"]}
            >
                <ReductionField
                    value={percent}
                    onChange={setPercent}
                    ariaLabel="Reduction, percent"
                />
            </FormGroup>
            <RadioGroup
                label="Apply to"
                selectedValue={applyTo}
                onChange={(event) =>
                    setApplyTo(event.currentTarget.value as BulkDownsizeSide)
                }
            >
                <Radio label={energyLabel} value="eris" />
                <Radio label={capacityLabel} value="nris" />
                <Radio label="Both" value="both" />
            </RadioGroup>
            <p className={classNames(Classes.TEXT_MUTED, Classes.TEXT_SMALL)}>
                {disabledReason ?? (
                    <>
                        Sets {sideLabel} reduction to <b>{percent}%</b> on{" "}
                        <b>{changes.length}</b> project
                        {changes.length === 1 ? "" : "s"}.{" "}
                        {mwDelta >= 0 ? "Removes" : "Restores"}{" "}
                        <b>{formatPower(Math.abs(mwDelta))}</b>.
                    </>
                )}
            </p>
            {erisBelowNrisCount > 0 && (
                <p
                    className={classNames(
                        Classes.TEXT_SMALL,
                        css["BulkDownsizePopover-warning"]
                    )}
                >
                    <Icon
                        icon={IconNames.WARNING_SIGN}
                        intent={Intent.WARNING}
                    />{" "}
                    {erisBelowNrisCount} project
                    {erisBelowNrisCount === 1 ? "" : "s"} would have more NRIS
                    MW than ERIS MW. {NRIS_EXCEEDS_ERIS_TARIFF}
                </p>
            )}
            <div className={css["BulkDownsizePopover-actions"]}>
                <Button
                    text="Cancel"
                    variant="minimal"
                    className={Classes.POPOVER_DISMISS}
                />
                <Tooltip
                    compact
                    content={disabledReason}
                    disabled={disabledReason === undefined}
                >
                    <Button
                        text="Apply"
                        intent={Intent.PRIMARY}
                        disabled={changes.length === 0}
                        onClick={() => onApply(changes)}
                    />
                </Tooltip>
            </div>
        </div>
    );
};
